<template>
  <div style="margin-bottom: 200px;">
    <h1 v-bg-typography="'h1'" :style="{ marginTop: '10px' }">Tags</h1>
    <div class="filters">
      <bg-field class="field" label="Info">
        <bg-select
          @change="selectInfo"
          :options="infoOptions"
          :value="activeTab"
        ></bg-select>
      </bg-field>

      <bg-field
        style="max-width: 250px"
        label="Tag"
        v-if="activeTab === 'Feedback'"
      >
        <bg-select
          label="name"
          :value="activeTag"
          :options="data"
          @change="selectTag"
        ></bg-select>
      </bg-field>

      <bg-field
        :style="`max-width: ${getWidth()}px;`"
        label="Data"
        v-if="activeTab != 'Compare'"
      >
        <div class="data" style="display: flex; gap: 5px">
          <div
            class="datacard"
            v-bind:class="
              activeEmotion === 'frustration' ? 'active-button' : ''
            "
            @click="activateEmotion('frustration')"
          >
            <p>Frustration</p>
          </div>
          <div
            class="datacard"
            v-bind:class="activeEmotion === 'joy' ? 'active-button' : ''"
            @click="activateEmotion('joy')"
          >
            <p>Joy</p>
          </div>
          <div
            class="datacard"
            v-bind:class="activeEmotion === 'attention' ? 'active-button' : ''"
            @click="activateEmotion('attention')"
          >
            <p>Attention</p>
          </div>
          <div
            v-if="activeTab === 'Feedback'"
            class="datacard"
            v-bind:class="activeEmotion === 'all' ? 'active-button' : ''"
            @click="activateEmotion('all')"
          >
            <p>All</p>
          </div>
        </div>
      </bg-field>
    </div>

    <bg-loader :dark="true" v-if="isLoading"></bg-loader>
    <div class="small" v-if="!isLoading && activeTab === 'Compare'">
      <bar-chart :options="options" :chartData="datacollection"></bar-chart>
    </div>

    <div
      v-if="!isLoading && activeTab === 'Ranking' && this.top3.length > 0"
      class="sub"
    >
      <h3 v-bg-typography="'h3'">Top 3 tags with most {{ activeEmotion }}</h3>
      <div class="top-three">
        <div
          class="card"
          v-for="(page, index) in this.top3"
          :key="index"
          v-bind:class="activeRankIndex === index ? 'active-card' : ''"
          @click="activeRankIndex = index"
        >
          <h2>
            <b>#{{ `${index + 1}. ` }}</b> {{ page.name }}
          </h2>
          <p style="margin-bottom: 15px">
            Experienced by <b>{{ page.participants }}</b> participants
          </p>
          <div
            class="bar"
            :style="
              `background: linear-gradient(${barWidth(page).peaks.degree}deg, ${
                barWidth(page).peaks.first
              } ${barWidth(page).peaks.peaks}%, ${
                barWidth(page).peaks.second
              } ${100 - barWidth(page).peaks.peaks}%);`
            "
          >
            <p>
              <b>{{ page.sps.length }}</b
              >x {{ activeEmotion }} feedbacks
            </p>
          </div>
        </div>
      </div>
      <h3 style="margin-top: 25px" v-bg-typography="'h3'">Why</h3>
      <div v-if="this.top3.length > 0">
        <bg-detail-card
          v-for="feedback in top3[this.activeRankIndex].sps"
          v-bind:key="feedback.id"
          v-bind="{ ...feedback }"
          :text="feedback.comment"
          :detail="feedback.emotion"
          @click.native="selectFeedback(feedback)"
          actionIcon="videos"
          actionText="Watch recording"
          :actionTextTarget="`Participant ${feedback.participant_id}`"
        />
      </div>
    </div>

    <div v-if="!isLoading && activeTab === 'Feedback'" class="sub">
      <div class="select-top">
        <p><b>(Select) Participant feedback</b></p>
        <br />
      </div>

      <div
        :class="{
          twogrid: $store.state.user.user.company.id != 2,
          threegrid: $store.state.user.user.company.id === 2,
        }"
      >
        <bg-button
          variant="primary"
          :text="
            selectedTags.length === feedbacks.length
              ? 'Deselect all'
              : 'Select all'
          "
          @click="selectAll"
          style="height: 46px"
        ></bg-button>

        <bg-select
          label="title"
          placeholder="Select an Insight to add selected feedback"
          v-model="selectedInsight"
          :options="insights"
          @input="selectInsight"
          :extraWide="true"
        ></bg-select>
        <bg-button
          :variant="selectedTags.length === 0 ? 'secondary' : 'primary'"
          text="add to insight"
          @click="addFeedbackToInsight"
          style="height: 46px"
        ></bg-button>
      </div>

      <div
        v-for="feedback in feedbacks"
        :key="feedback.id"
        class="feedback-list"
      >
        <div
          class="select"
          v-bind:class="{ active: selectedTags.includes(feedback.id) }"
          @click="select(feedback.id)"
        >
          <bg-icon :name="iconName(feedback.id)"></bg-icon>
        </div>
        <bg-detail-card
          :text="feedback.comment"
          :detail="feedback.emotion"
          @click.native="selectFeedback(feedback)"
          actionIcon="videos"
          :insights="feedback.insights"
          actionText="Watch recording"
          :actionTextTarget="`Participant ${feedback.participant_id}`"
        />
      </div>
      <p v-if="feedbacks.length === 0">No feedback found on this page!</p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "./styles.scss";

.small {
  max-width: 1000px;
}
</style>

<script>
import BgTypography from "@/directives/bg-typography";
import API from "@/api";
import BarChart from "./BarChart";
import BgField from "@/components/bg-field";
import BgSelect from "@/components/bg-select";
import BgInput from "@/components/bg-input";
import BgDetailCard from "@/components/bg-detail-card";
import BgLoader from "@/components/bg-loader";
import List from "@/components/list";
import BgIcon from "@/components/bg-icon";
import BgButton from "@/components/bg-button";
import generateInsight from "@/mixins/GenerateInsight.js";

export default {
  mixins: [generateInsight],
  directives: {
    BgTypography,
  },
  components: {
    BgIcon,
    BarChart,
    BgField,
    BgSelect,
    // BgInput,
    BgDetailCard,
    BgLoader,
    List,
    BgButton,
  },
  data() {
    return {
      activeComparison: "",
      activeEmotion: "frustration",
      activeRankIndex: 0,
      activeTab: "Ranking",
      activeTag: "",
      data: [],
      datacollection: {},
      infoOptions: ["Ranking", "Compare", "Feedback"],
      insights: [],
      isLoading: false,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: false,
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
              },
              stacked: true,
            },
          ],
          yAxes: [
            {
              stacked: true,
              ticks: {
                suggestedMin: 0,
              },
              scaleLabel: {
                display: true,
                labelString: "Indexed",
              },
            },
          ],
        },
      },
      optionsOfPp: [10, 9, 8, 7, 6, 5, 4, 3, 2, 1],
      minAmountOfPp: 0,
      minAmountOfTime: 0,
      selectedInsight: null,
      selectedTags: [],
      tags: [],
    };
  },
  created() {
    this.fetchData();
  },
  computed: {
    feedbacks: function() {
      let filtered = [];

      this.data.forEach((tag) => {
        let data = [];
        tag.significant_points.forEach((el) => {
          if (el.comment) {
            const insights = [];
            this.insights.forEach((insight) => {
              const ind = insight.significant_points_ids.findIndex(
                (o) => o === el.id
              );
              if (ind > -1) {
                insights.push(insight.title);
              }
            });
            el.tag = tag.name;
            el.insights = insights;
            data.push(el);
          }
        });
        data.forEach((point) => filtered.push(point));
      });
      if (this.activeEmotion != "all") {
        filtered = filtered.filter((el) => {
          return el.emotion === this.activeEmotion;
        });
      }
      filtered = filtered.filter((el) => {
        return el.tag === this.activeTag.name;
      });
      return filtered;
    },
    top3: function() {
      const emo = this.activeEmotion;
      let data = [];
      this.data.forEach((el) => data.push(el));
      data.forEach((tag) => {
        tag.count = tag.significant_points.filter((ob) => {
          return ob.emotion === emo;
        }).length;
        tag.sps = tag.significant_points.filter((ob) => {
          return ob.emotion === emo;
        });
        let participants = [];
        tag.sps.forEach((sp) => {
          if (!participants.includes(sp.participant_id)) {
            participants.push(sp.participant_id);
          }
        });
        tag.participants = participants.length;
      });

      data.sort((a, b) => (a.count < b.count ? 1 : b.count < a.count ? -1 : 0));
      return data.slice(0, 3);
    },
  },
  methods: {
    activateEmotion(val) {
      this.activeComparison = "Emotions";
      this.activeEmotion = val;
    },
    async addFeedbackToInsight() {
      this.selectedTags.forEach((t) => {
        this.selectedInsight.significant_points_ids.push(t);
      });

      await API.test.updateInsight(
        this.$route.params.id,
        this.selectedInsight.id,
        this.selectedInsight
      );
      this.selectedTags = [];
      this.selectInsight = null;
      this.fetchData();
    },
    barWidth(val) {
      let score = {
        direction: "",
        peaks: {
          peaks: 0,
          first: "#FF14A9",
          second: "transparent",
          degree: 90,
        },
      };

      if (this.activeEmotion === "frustration") {
        score.peaks.first = "#FF14A9";
      } else if (this.activeEmotion === "attention") {
        score.peaks.first = "#F1C40F";
      } else {
        score.peaks.first = "#00DEAB";
      }

      score.peaks.peaks = val.significant_points.length * 2;

      if (score.peaks.peaks < 50) {
        score.peaks.peaks = 100 - score.peaks.peaks;
        score.peaks.degree = 270;
        const first = score.peaks.first;
        score.peaks.first = score.peaks.second;
        score.peaks.second = first;
      }

      return score;
    },
    async fetchData() {
      this.isLoading = true;
      const res = await API.test.getTags(this.$route.params.id);
      this.data = res.tags;

      this.insights = [];
      const insights = await API.test.getInsights(this.$route.params.id);
      insights.data.insights.forEach((el) => {
        this.insights.push(el);
      });

      this.generateNewDataset();

      this.isLoading = false;
    },
    generateNewDataset() {
      this.isLoading = true;
      this.datacollection = {};
      const newDataset = {
        labels: [],
        datasets: [
          {
            label: "Frustration",
            data: [],
            backgroundColor: "#FF14A9",
          },
          {
            label: "Joy",
            data: [],
            backgroundColor: "#00DEAB",
          },
          {
            label: "Attention",
            data: [],
            backgroundColor: "#F1C40F",
          },
        ],
      };
      const newOptions = {
        responsive: true,
        maintainAspectRatio: false,
        legend: false,
        scales: {
          xAxes: [
            {
              stacked: true,
              gridLines: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              stacked: true,
              ticks: {
                suggestedMin: 0,
              },
              scaleLabel: {
                display: true,
                labelString: "Amount of feedbacks per emotion",
              },
            },
          ],
        },
      };
      if (!this.activeTag) {
        this.activeTag = this.data[0];
      }

      this.data.slice(0, 9).forEach((el) => {
        newDataset.labels.push(el.name);
        let frus = 0;
        let att = 0;
        let joy = 0;
        el.significant_points.forEach((sp) => {
          if (sp.emotion === "attention") {
            att = att + 1;
          } else if (sp.emotion === "frustration") {
            frus = frus + 1;
          } else if (sp.emotion === "joy") {
            joy = joy + 1;
          }
        });
        newDataset.datasets[0].data.push(frus);
        newDataset.datasets[1].data.push(joy);
        newDataset.datasets[2].data.push(att);
      });

      this.options = newOptions;
      this.datacollection = newDataset;
      this.isLoading = false;
    },
    getWidth() {
      if (this.activeTab === "Compare") {
        return 262;
      } else {
        return 260;
      }
    },
    iconName(val) {
      if (this.selectedTags.includes(val)) {
        return "checkCircle";
      } else {
        return "circle";
      }
    },
    select(val) {
      if (!this.selectedTags.includes(val)) {
        this.selectedTags.push(val);
      } else {
        const index = this.selectedTags.findIndex((el) => el === val);
        this.selectedTags.splice(index, 1);
      }
    },
    selectAll() {
      if (this.feedbacks.length === this.selectedTags.length) {
        this.selectedTags = [];
      } else {
        this.selectedTags = [];
        this.feedbacks.forEach((f) => {
          if (!this.selectedTags.includes(f.id)) {
            this.selectedTags.push(f.id);
          }
        });
      }
    },
    selectFeedback(val) {
      const routeObj = {
        path: `/test/${this.$route.params.id}/participant/${
          val.participant_id
        }/recording`,
        query: {
          timestamp: val.timestamp_top,
          sp_id: val.id,
        },
      };
      let routeData = this.$router.resolve(routeObj);
      window.open(routeData.href, "_blank");
    },
    selectInfo(val) {
      if (!this.noData) {
        this.activeTab = val;
        if (val != "Compare") {
          this.activeEmotion = "frustration";
          this.activeComparison = "Emotions";
        }
      }
    },
    selectInsight(val) {
      this.selectedInsight = val;
    },
    selectTag(val) {
      this.activeTag = val;
    },
  },
};
</script>
